/**
 * This file contains all functionality for the Find a TAVR Hospital
 * page. It allows the user to get TAVR Hospitals based on a location
 * they enter into the search input. They can learn more about each
 * Hospital by clicking it in the list.
 */

/**
 * jQuery
 *
 * @see http://api.jquery.com/
 *
 * @since 1.0.0
 */
import $ from 'jquery';

/**
 * A window.fetch JavaScript polyfill.
 *
 * @see https://github.com/github/fetch
 *
 * @since 1.0.0
 */
import 'whatwg-fetch';

/**
 * base64 is a robust base64 encoder/decoder that is fully compatible with atob() and btoa(),
 * written in JavaScript. The base64-encoding and -decoding algorithms it uses are fully RFC
 * 4648 compliant.
 *
 * @see https://www.npmjs.com/package/base-64
 *
 * @since 1.0.0
 */
import base64 from 'base-64';

/**
 * A collection of helper functions for use throughout the project.
 *
 * @since 1.0.0
 */
import * as helpers from "./modules/global/helpers";

/**
 * Wrapper for asynchronously used Google Maps API in browser.
 *
 * @see https://www.npmjs.com/package/google-maps
 *
 * @type {Object}
 */
import {Loader, LoaderOptions} from 'google-maps';

/**
 * Provide a Google Maps JavaScript API Key.
 *
 * @see https://developers.google.com/maps/documentation/javascript/get-api-key
 *
 * @type {String}
 */
// GoogleMapsLoader.KEY = "AIzaSyCUZCh_WOD8QCPjGU2ZdQoc1c-YMhsczRs";

/**
 * Load in the Google Places Library in order to utilize Autocomplete functionality.
 *
 * @see https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform
 *
 * @type {Array}
 */
// GoogleMapsLoader.LIBRARIES = ["places"];

/**
 * Object containing all Center Finder functionality.
 *
 * @since 1.0.0
 *
 * @type {Object}
 */
const centerFinder = {
	/**
	 * URL for the Find a TAVR Center API
	 * @type {String}
	 */
	fatcAPI: `${window.API_SETTINGS.ecUrl}/api/v1/fatc`,

	/**
	 * Placeholder for all active map markers.
	 * @type {Array}
	 */
	markers: [],

	/**
	 * Placeholder for all active map infowindows.
	 * @type {Array}
	 */
	infowindows: [],
	activeInfowindow: false,

	/**
	 * Track the search input's previous value for comparison.
	 */
	currentSearchValue: '',

	/**
	 * Initialize all Center Finder functionality.
	 *
	 * @since 1.0.0
	 */
	init() {

		/**
		 * UI Elements
		 */
		this.searchEl         = document.getElementById('js-find-center-search');
		this.predictionsEl    = document.getElementById('js-find-center-predictions');
		this.limitSelectEl    = document.getElementById('js-find-center-limit');
		this.resultsEl        = document.getElementById('js-find-center-results');
		this.resultsHeaderEl  = document.getElementById('js-find-center-results-header');
		this.detailsEl        = document.getElementById('js-find-center-details');
		this.backButtonEl     = document.getElementById('js-find-center-back-button');
		this.mapContainerEl   = document.getElementById('js-find-center-container');
		this.mapEl            = document.getElementById('js-find-center-map');
		this.alertContainerEl = document.getElementById('js-find-center-alert-container');

		this.bindEvents();

		/**
		 * Load the Google Maps API
		 */
		const options = {
			libraries: ['places']
		};
		const loader = new Loader('AIzaSyCUZCh_WOD8QCPjGU2ZdQoc1c-YMhsczRs', options);
		let that = this;
		loader.load().then(function (google) {
			that.google = google;
			that.initSearch();
			that.initMap();
			that.initResultsList();
			that.geolocate();
			that.gtmEvents();
		});
	},

	/**
	 * Bind certain events so that the 'this' keyword refers to this object,
	 * instead of what the event normally binds to.
	 *
	 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Function/bind
	 *
	 * @since 1.0.0
	 */
	bindEvents() {
		this.handleLimitChange             = this.handleLimitChange.bind(this);
		this.handleCenterClick             = this.handleCenterClick.bind(this);
		this.hideCenterDetails             = this.hideCenterDetails.bind(this);
		this.toggleEmailForm               = this.toggleEmailForm.bind(this);
		this.recenterMap                   = this.recenterMap.bind(this);
		this.sendResultsEmail              = this.sendResultsEmail.bind(this);
		this.updateAutocompletePredictions = this.updateAutocompletePredictions.bind(this);
		this.handleInputFocus              = this.handleInputFocus.bind(this);
		this.handleInputBlur               = this.handleInputBlur.bind(this);
		this.handleKeyboardNavigation      = this.handleKeyboardNavigation.bind(this);
		this.handlePredictionClick         = this.handlePredictionClick.bind(this);
		this.handlePlaceSelect             = this.handlePlaceSelect.bind(this);
	},

	/**
	 * Initialize Google's AutocompleteService and Geocoder classes for later use,
	 * and register all search related event listeners.
	 *
	 * @since 1.0.0
	 */
	initSearch() {
		this.autocompleteService = new this.google.maps.places.AutocompleteService();
		this.geocoder = new this.google.maps.Geocoder();
		this.searchEl.addEventListener('input', this.updateAutocompletePredictions);
		this.searchEl.addEventListener('focusin', this.handleInputFocus);
		this.searchEl.addEventListener('blur', this.handleInputBlur);
		document.addEventListener('keydown', this.handleKeyboardNavigation);
		document.addEventListener('mousedown', this.handlePredictionClick);
		document.addEventListener('touchstart', this.handlePredictionClick);
		this.limitSelectEl.addEventListener('change', this.handleLimitChange);
	},

	/**
	 * Initialize the map.
	 *
	 * @since 1.0.0
	 */
	initMap() {
		this.map = new this.google.maps.Map(this.mapEl, {
			zoom: 4,
			center: new this.google.maps.LatLng(39.842286020743394, -99.404296875) // centered on United States
		});

		this.google.maps.event.addListener(this.map, 'idle', () => {
			this.mapCenter = this.map.getCenter();
		});

		this.google.maps.event.addDomListener(window, 'resize', () => {
			this.google.maps.event.trigger(this.map, 'resize');
		});

		this.google.maps.event.addListener(this.map, 'resize', this.recenterMap);
	},

	/**
	 * Initialize the results list.
	 *
	 * @since 1.0.0
	 */
	initResultsList() {
		this.resultsEl.addEventListener('click', this.handleCenterClick);
		this.backButtonEl.addEventListener('click', this.hideCenterDetails);
	},

	/**
	 * Use the Geolocation API to get the user's current location in order to
	 * center the map, and set the Autocomplete bounds. Position is stored in
	 * localStorage, so that on subsequent views, the map will instantly load
	 * in the right location.
	 *
	 * @since 1.0.0
	 */
	geolocate() {
		/**
		 * Make sure the user's browser supports geolocation before continuing.
		 */
		if(navigator.geolocation) {
			/**
			 * Check if tavrPosition is in localStorage, and if so,
			 * use that to show centers close to the user's location.
			 *
			 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
			 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/parse
			 */
			if(localStorage.tavrPosition) {
				console.log("localstorage");
				this.userPosition = JSON.parse(localStorage.tavrPosition);
				this.useCurrentLocation();
			}

			/**
			 * Check if you're on the Covid-19 Landing Page, then force the posiiton/center of map
			 *
			 */
			// if(document.body.classList.contains('page-id-43746') || document.body.classList.contains('page-id-1188') || document.body.classList.contains('page-id-1189')) {
			// 	this.userPosition = {
			// 		lat: "34.052235",
			// 		lng: "	-118.243683",
			// 	}
			// 	this.useCurrentLocation();
			// }

			/**
			 * Otherwise, use the Geolcation API to get the users position.
			 * Then save that position into localStorage for subsequent page views.
			 *
			 * @see https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/getCurrentPosition
			 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify
			 */
			else {	
			 /**
				 * Store reference to this object for use within the functions below.
				 */
				const _this = this;

				/**
				 * Add loading class and message to components.
				 */
				this.mapContainerEl.classList.add('loading');
				this.searchEl.value = `Loading results near your location...`;

				/**
				 * Remove loading class and message from components.
				 */
				function stopLoading() {
					_this.mapContainerEl.classList.remove('loading');
					_this.searchEl.value = '';
				}

				/**
				 * Function to run when geolocation has run successfully.
				 * @param {Position} position
				 */
				function success(position) {
					stopLoading();

					/**
					 * Take only the values we need from the 'position' object, since,
					 * for some reason, it can't be converted to a string using JSON.stringify.
					 * @type {Object}
					 */
					_this.userPosition = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
						accuracy: position.coords.accuracy
					};

					_this.currentPosition = {
						lat: position.coords.latitude,
						lng: position.coords.longitude
					};

					_this.useCurrentLocation();

					/**
					 * Save the user's current position for later use.
					 */
					localStorage.setItem('tavrPosition', JSON.stringify(_this.userPosition));
				}

				/**
				 * Function to run when geolocation fails.
				 * @param {PositionError} error
				 */
				function error(error) {
					stopLoading();
					console.warn(`ERROR(${error.code}): ${error.message}`);
				}

				navigator.geolocation.getCurrentPosition(success, error);
			}
		}
	},

	/**
	 * Update the map and results using the user's current location.
	 *
	 * @since 1.0.0
	 */
	useCurrentLocation() {
		let limitValue = this.limitSelectEl.value;
		// if(document.body.classList.contains('page-id-43746') || document.body.classList.contains('page-id-1188') || document.body.classList.contains('page-id-1189')) {
		// 	limitValue = 20;
		// }
		this.getCenters(this.userPosition.lat, this.userPosition.lng, limitValue);
		this.searchEl.value = 'Currently using your location. Click to change.';
		this.searchEl.classList.add('has-current-location-message');
	},

	/**
	 * Clear out the "current location" placeholder message from the input on focus.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleInputFocus(e) {
		if(this.searchEl.classList.contains('has-current-location-message')) {
			this.searchEl.value = '';
			this.searchEl.classList.remove('has-current-location-message');
		}
	},

	/**
	 * Remove classes from the searchEl and predictionsEl when the searchEl loses focus.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleInputBlur(e) {
		if(!this.hasResults()) {
			this.showNoResultsAlert();
		} else {
			this.alertContainerEl.innerHTML = '';
		}
		setTimeout(() => {
			this.searchEl.classList.remove('focused');
			this.predictionsEl.classList.remove('active');
		}, 150);
	},

	/**
	 * Check to see if a prediction from the autocomplete dropdown was clicked,
	 * and if so, trigger this.handlePlaceSelect(), passing the event target.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handlePredictionClick(e) {
		if(e.target.classList.contains('js-ac-prediction')) {
			this.handlePlaceSelect(e.target);
		}
	},

	/**
	 * Enabled keyboard navigation of the autocomplete dropdown. User's can use the up and down
	 * arrows to highlight the prediction, and then can hit 'enter' to select it. Hitting the
	 * 'escape' key will close the dropdown.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleKeyboardNavigation(e) {
		if(this.searchEl.classList.contains('focused')) {
			switch(e.key) {
				case 'ArrowDown':
				case 'ArrowUp':
					e.preventDefault();
					const $predictions = $(this.predictionsEl).find('.js-ac-prediction');
					const $activePrediction = $(this.predictionsEl).find(`.js-ac-prediction.active`);
					let activeIndex = $predictions.index($activePrediction);

					/**
					 * Update activeIndex according to which arrow key was pressed.
					 */
					if(e.key === 'ArrowDown') {
						if(activeIndex === -1) {
							activeIndex = 0;
						} else if(activeIndex !== $predictions.length - 1) {
							activeIndex += 1;
						}
					} else {
						if(activeIndex > 0) {
							activeIndex -= 1;
						}
					}

					/**
					 * Remove the active class from the previous prediction, and add it to the new prediction.
					 */
					$activePrediction.removeClass('active');
					$(this.predictionsEl).find(`.js-ac-prediction:eq(${activeIndex})`).addClass('active');
					break;

				case 'Enter':
					this.handlePlaceSelect(this.predictionsEl.querySelector('.js-ac-prediction.active'));
					break;

				case 'Escape':
					this.searchEl.value = '';
					this.searchEl.blur();
					break;
			}
		}
	},

	/**
	 * Trigger a call to this.getCenters() when a prediction from the autocomplete dropdown is
	 * selected. If the node has a lat and lng in its dataset, then make the request immediately,
	 * otherwise, use the place-id in its dataset to get the lat and lng using Google's Geocoder class.
	 *
	 * @param {Node} prediction
	 */
	handlePlaceSelect(prediction) {
		this.searchEl.value = prediction.textContent;
		this.searchEl.blur();

		/**
		 * If the node has a lat and lng in its dataset, then make the request immediately.
		 */
		if(prediction.dataset.lat && prediction.dataset.lng) {
			let limitValue = this.limitSelectEl.value;
			// if(document.body.classList.contains('page-id-43746') || document.body.classList.contains('page-id-1188') || document.body.classList.contains('page-id-1189')) {
			// 	limitValue = 20;
			// }
			this.getCenters(prediction.dataset.lat, prediction.dataset.lng, limitValue);
		}

		/**
		 * Otherwise, use the place-id in its dataset to get the lat and lng using Google's Geocoder class.
		 */
		else if(prediction.dataset.placeId) {
			this.geocoder.geocode({'placeId': prediction.dataset.placeId}, (results, status) => {
				if(status === 'OK') {
					if (results[0]) {
						let limitValue = this.limitSelectEl.value;
						// if(document.body.classList.contains('page-id-43746') || document.body.classList.contains('page-id-1188') || document.body.classList.contains('page-id-1189')) {
						// 	limitValue = 20;
						// }
						this.getCenters(results[0].geometry.location.lat(), results[0].geometry.location.lng(), limitValue);
					} else {
						console.log('No results found');
					}
				} else {
					console.log('Geocoder failed due to: ' + status);
				}
			});
		}
	},

	/**
	 * Update the autocomplete search predictions using google's AutocompleteService and the
	 * FATC autocomplete api endpoint.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	updateAutocompletePredictions(e) {
		/**
		 * Add classes to searchEl and predictionsEl.
		 */
		this.searchEl.classList.add('focused');
		this.predictionsEl.classList.add('active');

		const searchValue = this.searchEl.value;

		/**
		 * Only proceed if the searchValue, when trimmed, doesn't match the previous search term.
		 * Eliminates extra requests when the user types a space.
		 */
		if(searchValue.trim() !== this.currentSearchValue) {

			/**
			 * Only proceed if the searchValue isn't an empty string to eliminate extra requests.
			 */
			if(searchValue !== '') {
				/**
				 * First, get predictions from the FATC autocomplete API. Then pass the predictions along,
				 * and get additional predictions from the Google AutocompleteService. Finally, pass all
				 * predictions on and build out the dropdown HTML.
				 *
				 * @type {Promise}
				 */
				fetch(`${this.fatcAPI}/autocomplete?search_term=${searchValue}`)
					.then(response => response.json())
					.then(
						(data) => {
							const fatcPredictions = data.results;
							/**
							 * Make request to Google's, AutocompleteService to get place predictions.
							 */
							const googleAutocompletePromise = new Promise((resolve, reject) => {
								this.autocompleteService.getPlacePredictions({
									input: searchValue,
									types: ['(regions)'],
									componentRestrictions: { country: 'US' }
								}, (googlePredictions, status) => {
									if(status === this.google.maps.places.PlacesServiceStatus.OK) {
										resolve(googlePredictions);
									} else {
										reject(Error(status));
									}
								});
							});

							/**
							 * If Google returns predictions, then use .concat() to append them to fatcPredictions,
							 * and return the merged array. Otherwise, just return the previous array of predictions
							 * returned from the FATC api.
							 */
							return googleAutocompletePromise.then(
								googlePredictions => {
									return fatcPredictions.concat(googlePredictions);
								},
								reason => {
									return fatcPredictions;
								}
							);
						},
						reason => {
							return reason;
						}
					)
					.then(
						(predictions) => {
							if(predictions.length > 0) {
								/**
								 * Placeholder for prediction dropdown HTML.
								 */
								let predictionsHtml = '';

								/**
								 * Loop through all FATC predictions and add them to predictionsHtml.
								 */
								helpers.forEach(predictions, (i, prediction) => {
									let className = 'js-ac-prediction dropdown-list__item';

									/**
									 * Add active class to the first prediction.
									 */
									if(i === 0) {
										className = `${className} active`;
									}

									/**
									 * Handle Google predictions (stripping out States)
									 */
									if(prediction.place_id) {
										if (! (prediction.hasOwnProperty('types') && prediction.types.includes('administrative_area_level_1')) ) {
											predictionsHtml += `<div class="${className}" data-place-id="${prediction.place_id}">${prediction.description}</div>`;
                                        					}
									}

									/**
									 * Handle FATC predictions.
									 */
									else if(prediction.lat && prediction.lng) {
										predictionsHtml += `<div class="${className}" data-lat="${prediction.lat}" data-lng="${prediction.lng}">${prediction.name}</div>`;
									}
								});

								/**
								 * Update the prediction dropdown's HTML.
								 */
								this.predictionsEl.innerHTML = predictionsHtml;
							} else {
								/**
								 * Add a helpful message if no results are found.
								 */
								this.predictionsEl.innerHTML = `<div id="js-fath-no-results-item" class="dropdown-list__item">No results found</div>`;
							}

							return predictions;
						},
						(reason) => {
							console.log(reason);
							return reason;
						}
					);
			}

			/**
			 * Keep track of the new search value for later comparison.
			 */
			this.currentSearchValue = searchValue.trim();
		}
	},

	/**
	 * Use the new Place lat and lng to get new centers closeby.
	 *
	 * @since 1.0.0
	 */
	handleLimitChange(e) {
		if(this.currentPosition || this.hasResults()) {
			/**
			 * Use the user's current location, if available, to get the closest centers.
			 */
			this.getCenters(this.currentPosition.lat, this.currentPosition.lng, this.limitSelectEl.value);
			this.alertContainerEl.innerHTML = '';
		} else {
			this.showNoResultsAlert();
		}
	},

	/**
	 * Determine if there are any valid search results.
	 *
	 * @since 1.0.0
	 */
	hasResults() {
		const noResultsItem = document.getElementById('js-fath-no-results-item');
		if(noResultsItem) {
			return false;
		}

		const results = document.querySelectorAll('#js-find-center-predictions .dropdown-list__item');
		if(results.length === 0) {
			return false;
		}

		return true;
	},

	/**
	 * Show the no results message.
	 *
	 * @since 1.0.0
	 */
	showNoResultsAlert() {
		this.alertContainerEl.innerHTML = `<div class="alert alert--info alert--inline">
			<span class="alert__icon"><svg><use xlink:href="${emagine.spriteUrl}#:icon-important"></use></svg></span>
			<span class="alert__content">Please enter a valid zip code, city/state, or hospital name.</span>
		</div>`;
	},

	/**
	 * Center the map on the given latitude and longitude coordinates.
	 *
	 * @since 1.0.0
	 *
	 * @param {Number} lat Latitude
	 * @param {Number} lng Longitude
	 */
	centerMapOnCoords(lat, lng) {
		this.map.setCenter(new this.google.maps.LatLng(lat, lng));
	},

	/**
	 * Recenter the map on the current center.
	 *
	 * @since 1.0.0
	 */
	recenterMap() {
		this.map.setCenter(this.mapCenter);
	},

	/**
	 * Make a request to the TAVR API using those coordinates to return the closest TAVR Centers.
	 *
	 * @since 1.0.0
	 *
	 * @param {Number} lat Latitude
	 * @param {Number} lng Longitude
	 * @param {Number} limit Number of results to return with the request.
	 */
	getCenters(lat, lng, limit = 20) {
		fetch(`${this.fatcAPI}?lat=${lat}&lng=${lng}&limit=${limit}`)
			.then(response => response.json())
			.then(data => {
				this.list = {
					centers: data.results,
					downloadURL: `${this.fatcAPI}/download?lat=${lat}&lng=${lng}&limit=${limit}`,
					printURL: `${this.fatcAPI}/print?lat=${lat}&lng=${lng}&limit=${limit}`
				};
				this.currentPosition = { lat, lng };
				this.updateMap();
				this.alertContainerEl.innerHTML = '';
			});
	},

	/**
	 * Get a center's formatted address.
	 *
	 * @since 1.0.0
	 *
	 * @param {object} center
	 */
	getAddress(center) {
		let address = '';

		address += center.address_1 ? center.address_1 : "";
		address += center.address_2 ? ", " + center.address_2 : "";
		address += center.address_3 ? ", " + center.address_3 : "";

		address += center.city ? "<br>" + center.city : "";
		address += center.state ? ", " + center.state : "";
		address += center.zip_code ? " " + center.zip_code : "";

		return address;
	},

	/**
	 * Get the Google Maps direction link based on the address provided.
	 * If the user has allowed access to their location, then add it as the origin point.
	 *
	 * @since 1.0.0
	 *
	 * @param {string} address
	 * @return {string} Formatted Google Maps direction link.
	 */
	getDirectionsUrl(address) {
		const origin = this.userPosition ? `${this.userPosition.lat},${this.userPosition.lng}` : '';
		const destination = encodeURIComponent(address.replace('<br>', ' '));
		return `https://www.google.com/maps/dir/?api=1&${origin ? `origin=${origin}&` : ''}destination=${destination}`;
	},

	/**
	 * Add center details HTML to the detailsEl, setup email events, slide map into view and center on center pin.
	 *
	 * @since 1.0.0
	 *
	 * @param {Number} index The index of the center in this.list.centers
	 */
	loadCenter(index) {
		/**
		 * Use the passed in index value to get the center, marker, and infowindow.
		 */
		const center = this.list.centers[index];
		const marker = this.markers[index];
		const infowindow = this.infowindows[index];

		/**
		 * Get the center's formatted address.
		 */
		const address = this.getAddress(center);

		// console.log(center);

		/**
		 * Replace the curent innerHTML with the new center's HTML.
		 */
		this.detailsEl.innerHTML = `<div class="location">
			${center.image.url ? `<img class="location__media" src="${center.image.url}" alt="" />` : ``}
			<header class="location__header location__section">
				${center.logo.url && center.logo.url.indexOf('/default/logo.png') === -1 ? `<img class="location__logo" src ="${center.logo.url}" alt="">` : ``}
				${center.name ? `<div class="location__title">${center.name}</div>` : ``}
				${center.secondary_name ? `<div class="location__subtitle">${center.secondary_name}</div>` : ``}`;

				if(center.accepting_patients) {
					this.detailsEl.innerHTML += `<div class="location__featured">Currently Accepting Patients</div>`;
				}

				this.detailsEl.innerHTML += `<address class="location__address">
					${address}
					<div><a id="js-fath-listing-directions-link" href="${this.getDirectionsUrl(address)}" target="_blank" rel="noopener noreferrer">Get Directions</a></div>
				</address>
			</header>
			${(center.phone || center.email || center.website) && `<div class="location__contact location__section">
				${center.phone ? `<div><a id="js-fath-phone-link" href="tel:${center.phone}">${center.phone}</a></div>` : ``}
				${center.email ? `<div><a id="js-fath-listing-email-link" href="mailto:${center.email}" target="_blank" rel="noopener noreferrer">${center.email}</a></div>` : ``}
				${center.website ? `<div><a id="js-fath-listing-website-link" href="${center.website}" target="_blank" rel="noopener noreferrer">Visit Website</a></div>` : ``}
			</div>`}
			${center.team_members ? `<div class="location__team location__section">
				<ul class="media-list">
					${center.team_members.map(person => `<li class="media-item">
						<div class="media-item__media">
							<img src="${person.image.url}" alt="" />
						</div>
						<div class="media-item__body">
							<div class="media-item__title">${person.first_name} ${person.last_name}${person.suffix && ` ${person.suffix}`}</div>
							${person.role && `<div class="media-item__subtitle">${person.role}</div>`}
						</div>
					</li>`).join('')}
				</ul>
			</div>` : ``}
			<div class="location__actions location__section">
				<div class="btn-group">
					<a id="js-fath-listing-download-button" class="btn btn--block" href="${this.fatcAPI}/download?lat=${center.lat}&lng=${center.lng}&limit=1">
						<span class="btn__icon">
							<svg><use xlink:href="${emagine.spriteUrl}#:icon-download"></use></svg>
						</span>
						<span class="btn__text">Download</span>
					</a>
					<a id="js-fath-listing-print-button" class="btn btn--block" href="${this.fatcAPI}/print?lat=${center.lat}&lng=${center.lng}&limit=1" target="_blank" rel="noreferrer noopener">
						<span class="btn__icon">
							<svg><use xlink:href="${emagine.spriteUrl}#:icon-printer"></use></svg>
						</span>
						<span class="btn__text">Print</span>
					</a>
					<button id="js-fath-listing-email-button" class="btn btn--block">
						<span class="btn__icon">
							<svg><use xlink:href="${emagine.spriteUrl}#:icon-email"></use></svg>
						</span>
						<span class="btn__text">Email</span>
					</button>
				</div>
				<form id="js-fath-listing-email-form" class="location__email-form" data-api-url="${this.fatcAPI}/email?lat=${center.lat}&lng=${center.lng}&limit=1">
					<div class="location__email-form-inner">
						<div>
							<label for="tavr-email">Email Address</label>
							<input id="tavr-email" type="email" name="email" required />
						</div>
						<div>
							<label for="tavr-subject-line">Subject Line</label>
							<input id="tavr-subject-line" type="text" name="email" value="TAVR Center Search Results" required />
						</div>
						<button id="js-fath-listing-email-form-submit-button" class="btn btn--block" type="submit">Send Email</button>
					</div>
				</form>
			</div>
		</div>`;

		/**
		 * Add events to newly added email button and form.
		 */
		this.emailButton = document.getElementById('js-fath-listing-email-button');
		this.emailButton.addEventListener('click', this.toggleEmailForm);
		this.emailForm = document.getElementById('js-fath-listing-email-form');
		this.emailForm.addEventListener('submit', this.sendResultsEmail);

		/**
		 * Add class to map container that slides the details into view.
		 */
		this.mapContainerEl.classList.add("has-details");

		/**
		 * Close the active infowindow if it exists.
		 */
		if(this.activeInfowindow) {
			this.activeInfowindow.close();
		}

		/**
		 * Open the target infowindow.
		 */
		infowindow.open(this.map, marker);

		/**
		 * Set the target infowindow as the activeInfowindow.
		 */
		this.activeInfowindow = infowindow;

		/**
		 * Center map on marker and zoom in.
		 */
		this.map.setCenter(marker.getPosition());
		this.map.setZoom(14);
	},

	/**
	 * Remove class that slides center details out of view.
	 *
	 * @since 1.0.0
	 */
	hideCenterDetails() {
		this.map.fitBounds(this.map.bounds);
		this.mapContainerEl.classList.remove("has-details");
		this.activeInfowindow.close();

		/**
		 * Scroll the detailsEl to the top so it's posistioned properly when the next center is opened.
		 * Doing this on a timeout to avoid a flicker where the element jumps to the top before the slide
		 * transition can complete.
		 */
		setTimeout(() => {
			this.detailsEl.scrollTop = 0;
		}, 300);
	},

	/**
	 * Toggle class that shows/hides the email form.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	toggleEmailForm(e) {
		/**
		 * Check if we have an event object before proceeding. triggerEmailAlert() calls
		 * this function without the 'e' parameter.
		 */
		if(e) {
			const isEmailButton = $(e.target).closest(this.emailButton).length > 0;
			/**
			 * Remove any previous email alerts.
			 */
			if(isEmailButton && this.alert) {
				this.alert.remove();
			}
		}

		this.emailForm.classList.toggle('visible');
		$(this.detailsEl).animate({
			scrollTop: this.detailsEl.scrollHeight
		}, 250);
	},

	/**
	 * Make request to the /email endpoint of the FATC API that sends an email to the provided
	 * e-mail address along with custom subject line.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	sendResultsEmail(e) {
		e.preventDefault();
		const form = e.target;
		const email = form.elements['tavr-email'].value;
		const subject = form.elements['tavr-subject-line'].value;
		const apiURL = encodeURI(`${form.dataset.apiUrl}&email=${email}&subject=${subject}`);

		if(apiURL) {
			const emailPromise = new Promise((resolve, reject) => {
				const request = new XMLHttpRequest();
				request.open('GET', apiURL);

				request.onload = () => {
					if(request.status >= 200 && request.status < 400) {
						resolve(request.responseText);
					} else {
						reject(Error(request.statusText));
					}
				}

				request.onerror = () => {
					reject(Error('Network Error'));
				};

				request.send();
			});

			/**
			 * Show an alert message to the user based on success or failure.
			 */
			emailPromise.then(
				value => {
					const alertContent = `<span class="alert__icon"><svg><use xlink:href="${emagine.spriteUrl}#:icon-checkmark"></use></svg></span><span class="alert__text">Email sent!</span>`
					this.triggerEmailAlert(alertContent, 'alert--success');
				},
				reason => {
					const alertContent = `<span class="alert__icon"><svg><use xlink:href="${emagine.spriteUrl}#:icon-error"></use></svg></span><span class="alert__text">Email failed to send!</span>`
					this.triggerEmailAlert(alertContent, 'alert--danger');
				}
			);
		}
	},

	/**
	 * Add an alert after the email form.
	 *
	 * @since 1.0.0
	 *
	 * @param {String} content String of HTML to add to the alert.
	 * @param {String} className Class to add to the alert.
	 */
	triggerEmailAlert(content, className) {
		/**
		 * Close the email form.
		 */
		this.toggleEmailForm();

		/**
		 * Create the alert element, and add classes.
		 */
		this.alert = document.createElement('div');
		this.alert.classList.add('alert', className, 'loading');

		/**
		 * Create alert content element and add classes and content.
		 */
		const alertContent = document.createElement('div');
		alertContent.classList.add('alert__content');
		alertContent.innerHTML = content;

		/**
		 * Add alertContent to alert.
		 */
		this.alert.appendChild(alertContent);

		/**
		 * Insert the alert after the email form.
		 */
		helpers.insertAfter(this.alert, this.emailForm);

		/**
		 * Show loading animation for a second, and then remove the loading class.
		 */
		setTimeout(() => {
			this.alert.classList.remove('loading');
			$(this.detailsEl).delay(250).animate({
				scrollTop: this.detailsEl.scrollHeight
			}, 250);
		}, 1500);
	},

	/**
	 * Sets centers on top
	 *
	 * @since 1.0.0
	 *
	 * @param {array} centers
	 */
	setFeaturedCenters(centers) {
		helpers.forEach(centers, (i, center) => {
			if(center.accepting_patients) {
				let featuredCenter = centers.splice(i, 1);
				centers.unshift(featuredCenter[0]);
			}
		})
		return centers;
	},

	/**
	 * Add the new centers to the results list and their markers to the map.
	 *
	 * @since 1.0.0
	 *
	 * @param {array} centers
	 */
	addNewCenters() {
		let html = "";

		/**
		 * Reset existing map bounds with new LatLngBounds object.
		 */
		this.map.bounds = new this.google.maps.LatLngBounds();

		// if(document.body.classList.contains('page-id-43746') || document.body.classList.contains('page-id-1188') || document.body.classList.contains('page-id-1189')) {
			// this.list.centers = this.setFeaturedCenters(this.list.centers);
		// }

		helpers.forEach(this.list.centers, (i, center) => {
			/**
			 * Add marker for center.
			*/

			if(center.accepting_patients) {
				this.markers[i] = new this.google.maps.Marker({
					position: new this.google.maps.LatLng(center.lat, center.lng),
					map: this.map,
					icon: "https://newheartvalve.com/wp-content/uploads/2020/04/TAVR_Avil_Map_Pin.png"
				});
			} else {
				this.markers[i] = new this.google.maps.Marker({
					position: new this.google.maps.LatLng(center.lat, center.lng),
					map: this.map
				});
			}

			/**
			 * Extend the bounds to include the marker.
			 */
			this.map.bounds.extend(this.markers[i].getPosition());

			/**
			 * Setup event to load the center details when the marker is clicked.
			 */
			this.google.maps.event.addListener(this.markers[i], 'click', () => {
				this.loadCenter(i);
			});

			/**
			 * Add corresponding infowindow to marker.
			 */
			this.infowindows[i] = new this.google.maps.InfoWindow({
				content: center.name
			});

			/**
			 * Add event to hide center details and zoom out to bounds when infowindow was closed.
			 */
			this.google.maps.event.addListener(this.infowindows[i], 'closeclick', this.hideCenterDetails);

			/**
			 * Build center list item html.
			 */
			html += `<li class="location">
				<div class="location__title">${center.name}</div>`;

			if(center.accepting_patients) {
				html += `<div class="location__featured">Currently Accepting Patients</div>`;
			}

			html += `<address class="location__address">${this.getAddress(center)}</address>`;
			html += `<div class="location__distance">${center.distance.toFixed(1)} mi</div>`;
			html += `</li>`;
		});

		/**
		 * Replace resultsEl html with our new html.
		 */
		this.resultsEl.innerHTML = html;

		this.resultsHeaderEl.innerHTML = `<div>
			<div class="h6">Showing the ${this.list.centers.length} closest TAVR Hospitals</div>
			<a id="js-fath-list-print" class="link"href="${this.list.printURL}" target="_blank" rel="noopener noreferrer">
				<span class="link__icon">
					<svg><use xlink:href="${emagine.spriteUrl}#:icon-printer"></use></svg>
				</span><span class="link__text">Print</span>
			</a>
			<a id="js-fath-list-download" class="link" href="${this.list.downloadURL}">
				<span class="link__icon">
					<svg><use xlink:href="${emagine.spriteUrl}#:icon-download"></use></svg>
				</span><span class="link__text">Download</span>
			</a>
		</div>`;

		/**
		 * Make sure all the markers fit within the bounds of the map.
		 */
		this.map.setCenter(this.map.bounds.getCenter());
		this.map.fitBounds(this.map.bounds);
	},

	/**
	 * Remove the current centers from the results list and their markers from the map.
	 *
	 * @since 1.0.0
	 */
	clearOldCenters() {
		if (this.markers) {
			helpers.forEach(this.markers, (i, marker) => {
				marker.setMap(null);
			});

			this.markers = [];
		}

		/**
		 * Remove any added classes from the map container.
		 */
		this.mapContainerEl.classList.remove("has-details");
	},

	handleCenterClick(e) {
		// const targetEl = e.target.closest('.location');
		const targetEl = $(e.target).closest('.location').get(0);
		const centerEls = this.resultsEl.querySelectorAll('.location');

		const index = helpers.forEach(centerEls, (i, centerEl) => {
			if(centerEl === targetEl) {
				this.loadCenter(i);
			}
		});
	},

	/**
	 * Use the results returned from the TAVR API to render the closest
	 * TAVR Centers to the map, removing previously displayed centers.
	 *
	 * @since 1.0.0
	 *
	 * @param {Array} centers Array of Center objects returned from the TAVR API.
	 */
	updateMap() {
		if(this.list.centers) {
			this.clearOldCenters();
			this.addNewCenters();
			if(!this.mapContainerEl.classList.contains('has-results')) {
				this.mapContainerEl.classList.add("has-results");
			}
		}
	},

	gtmEvents() {
		window.dataLayer = window.dataLayer || [];

		/**
		 * List Print GTM Event.
		 */
		$(document).on('click', '#js-fath-list-print', function() {
			dataLayer.push({ 'event' : 'FATC Search - Print Listings' });
		});

		/**
		 * List Download GTM Event.
		 */
		$(document).on('click', '#js-fath-list-download', function() {
			dataLayer.push({ 'event' : 'FATC Search - Download Listings' });
		});

		/**
		 * List Download GTM Event.
		 */
		$(document).on('click', '#js-find-center-results .location', function() {
			dataLayer.push({ 'event' : 'FATC Search - Show Listing Detail' });
		});

		/**
		 * Hospital Details Directions Link GTM Event.
		 */
		$(document).on('click', '#js-fath-listing-directions-link', function() {
			dataLayer.push({ 'event' : 'FATC Search - Click Get Directions' });
		});

		/**
		 * Hospital Details Email Link GTM Event.
		 */
		$(document).on('click', '#js-fath-listing-email-link', function() {
			dataLayer.push({ 'event' : 'FATC Search - Click Listing Email' });
		});

		/**
		 * Hospital Details Website Link GTM Event.
		 */
		$(document).on('click', '#js-fath-listing-website-link', function() {
			dataLayer.push({ 'event' : 'FATC Search - Click Listing Website' });
		});

		/**
		 * Hospital Details Download Button GTM Event.
		 */
		$(document).on('click', '#js-fath-listing-download-button', function() {
			dataLayer.push({ 'event' : 'FATC Search - Click Download Listing' });
		});

		/**
		 * Hospital Details Print Button GTM Event.
		 */
		$(document).on('click', '#js-fath-listing-print-button', function() {
			dataLayer.push({ 'event' : 'FATC Search - Click Print Listing' });
		});

		/**
		 * Hospital Details Email Button GTM Event.
		 */
		$(document).on('click', '#js-fath-listing-email-button', function() {
			dataLayer.push({ 'event' : 'FATC Search - Click Share Email' });
		});

		/**
		 * Hospital Details Email Submit Button GTM Event.
		 */
		$(document).on('click', '#js-fath-listing-email-form-submit-button', function() {
			dataLayer.push({ 'event' : 'FATC Search - Click Submit Share Email' });
		});
	}
};

centerFinder.init();